/**
 * EFT-fee buffer — customer-facing price uplift.
 *
 * When a quote has the EFT buffer enabled (`includeEftFee` + a positive
 * `eftFeeRate`), the price we quote the customer is uplifted by the rate so the
 * card-processor fee is absorbed into the price rather than shown as a separate
 * surcharge line. The backend bakes this into every invoice line at
 * promotion-time (`_bake_in_eft_fee` in `quote_service.py`); these helpers
 * mirror it on the frontend so the quote-editor subtotals/totals AND the
 * customer preview show the same figure the customer will be invoiced.
 *
 * Negative lines (bundle discount, diagnostics credit, any negative item) are
 * never uplifted — see `applyEft`.
 *
 * IMPORTANT: the uplift is a *display/total* transform only. It is NEVER folded
 * into a stored `sellPrice`, so per-line margin / GP% (which read `sellPrice`
 * vs cost) are unaffected — the EFT amount stays out of the margin calculation.
 */

const round2 = (n: number): number => Math.round(n * 100) / 100;

/** Uplift multiplier for a quote's EFT settings — 1 when the buffer is off. */
export const eftMultiplier = (
  includeEftFee: boolean,
  eftFeeRate: number,
): number => (includeEftFee && eftFeeRate > 0 ? 1 + eftFeeRate : 1);

/**
 * Apply the EFT buffer to a customer-facing amount, rounded to 2dp. No-op
 * (returns the input rounded) when the buffer is off. Applied per line where
 * the customer sees itemised lines, so the shown lines sum to the quoted total
 * — matching the backend's per-line bake.
 *
 * A negative amount is never uplifted: the bundle discount, the diagnostics
 * credit and any negative item are fixed amounts off, and `_bake_in_eft_fee`
 * skips them. Pass a credit *as a negative* (or not at all) — passing its
 * positive magnitude here would uplift it.
 */
export const applyEft = (
  amount: number,
  includeEftFee: boolean,
  eftFeeRate: number,
): number =>
  amount < 0
    ? round2(amount)
    : round2(amount * eftMultiplier(includeEftFee, eftFeeRate));
